import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import {
  UserCircleIcon,
  MapPinIcon,
  BookmarkIcon,
  ArrowRightOnRectangleIcon
} from '@heroicons/react/24/outline';

const Container = styled.div`
  position: relative;
`;

const UserButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: transparent;
  border: 1px solid #e0e0e0;
  border-radius: 24px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f5f5f5;
    border-color: #d0d0d0;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: scale(0.98);
  }
`;

const Avatar = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
`;

const DefaultAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 14px;
`;

const UserName = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #333;
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: 768px) {
    display: none;
  }
`;

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  min-width: 240px;
  z-index: 1000;
  overflow: hidden;
`;

const DropdownHeader = styled.div`
  padding: 16px;
  border-bottom: 1px solid #f0f0f0;
  background: linear-gradient(135deg, #667eea10 0%, #764ba210 100%);
`;

const UserEmail = styled.div`
  font-size: 12px;
  color: #666;
  margin-top: 4px;
  word-break: break-all;
`;

const DropdownSection = styled.div`
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }
`;

const DropdownItem = styled.button`
  padding: 12px 16px;
  background: transparent;
  border: none;
  text-align: left;
  font-size: 14px;
  color: #333;
  cursor: pointer;
  transition: background 0.2s ease;
  display: flex;
  align-items: center;
  gap: 12px;

  svg {
    width: 20px;
    height: 20px;
    color: #666;
  }

  &:hover {
    background: #f5f5f5;

    svg {
      color: #667eea;
    }
  }

  &.logout {
    color: #d32f2f;

    svg {
      color: #d32f2f;
    }

    &:hover {
      background: #ffebee;
    }
  }
`;

/**
 * User Menu Component
 * Displays logged-in user's avatar and name with dropdown menu
 */
const UserMenu = () => {
  const { user, logout } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  if (!user) {
    return null;
  }

  const handleLogout = async () => {
    setIsOpen(false);
    await logout();
    navigate('/');
  };

  const handleNavigation = (path) => {
    setIsOpen(false);
    navigate(path);
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name[0].toUpperCase();
  };

  return (
    <Container ref={dropdownRef}>
      <UserButton onClick={() => setIsOpen(!isOpen)} aria-label="User menu">
        {user.picture ? (
          <Avatar src={user.picture} alt={user.name || user.email} />
        ) : (
          <DefaultAvatar>{getInitials(user.name || user.email)}</DefaultAvatar>
        )}
        <UserName>{user.name || user.email}</UserName>
      </UserButton>

      {isOpen && (
        <Dropdown>
          <DropdownHeader>
            <div style={{ fontWeight: 600, fontSize: '14px', color: '#333' }}>
              {user.name || 'User'}
            </div>
            <UserEmail>{user.email}</UserEmail>
          </DropdownHeader>

          <DropdownSection>
            <DropdownItem onClick={() => handleNavigation('/profile')}>
              <UserCircleIcon />
              <span>Profile</span>
            </DropdownItem>
            <DropdownItem onClick={() => handleNavigation('/itineraries')}>
              <MapPinIcon />
              <span>My Itineraries</span>
            </DropdownItem>
            <DropdownItem onClick={() => handleNavigation('/saved')}>
              <BookmarkIcon />
              <span>Saved Places</span>
            </DropdownItem>
          </DropdownSection>

          <DropdownSection>
            <DropdownItem onClick={handleLogout} className="logout">
              <ArrowRightOnRectangleIcon />
              <span>Logout</span>
            </DropdownItem>
          </DropdownSection>
        </Dropdown>
      )}
    </Container>
  );
};

export default UserMenu;
